import { createCssCompNode } from '../cssCompNode'
import type {
	Component,
	ComponentOverride,
	Docking,
	StripColumnsContainerDefinition,
	WRichTextProperties,
} from '@wix/thunderbolt-becky-types'
import { CompNode, createCompNode, envRefs } from '@wix/thunderbolt-catharsis'
import _ from 'lodash'
import { hasResponsiveLayout } from '../../utils/hasResponsiveLayout'
import { DEFAULT_DESKTOP_SITE_WIDTH, MOBILE_SITE_WIDTH, SOSP_CONTAINER_ID } from '../../utils/constants'
import { SlideShowContainerDefinition } from '@wix/thunderbolt-components-native/dist'
import { withModes } from '../modesCompNode'
import {
	getVisibility,
	px,
	areCssAttributesEqual,
	getDifferingAttributes,
	mergeSelectors,
} from '../../utils/selectorsUtils'
import { CompsLayout, RenderableCompMeasures } from './compsMeasuresCss.types'
import { isContainer } from '../../utils/is'
import type { FeatureRefs } from '../cssFeatures.types'
import { DEFAULT_DATA_MODE, HOVER_DATA_MODE } from '@wix/thunderbolt-commons'
import { CssObject, SelectorObj } from '../../shared.types'

const excludedComponents = new Set<string>(
	process.env.PACKAGE_NAME === 'thunderbolt-ds' ? ['PayPalButton', 'QuickActionBar'] : ['AppController']
)

const OVERRIDEN_PROPS = ['layout', 'isHiddenByModes'] as Array<keyof ComponentOverride>
const AUTO_HEIGHT = { height: 'auto' } as const
const AUTO_WIDTH = { width: 'auto' } as const
const DEFAULT_SELECTOR = ''
const SIZE = ['height', 'width']

const FULL_WIDTH_MOBILE_SITE_MEASURES: CompsLayout = {
	left: '0',
	'margin-left': '0',
	width: px(MOBILE_SITE_WIDTH),
}

// SHOULD BE DELETED AFTER MAPPER IS MERGED - https://github.com/wix-private/thunderbolt/pull/27141
const isComponentSlideShow = (component: Component) =>
	component.componentType === 'SlideShowContainer' ||
	component.componentType === 'StripContainerSlideShow' ||
	component.componentType === 'BoxSlideShow'

const getFullWidthAfterMarginsSubtraction = (layout: Component['layout'], siteMargin: number = 0) => {
	const leftMargin = getDockMargin(layout, 'left')
	const rightMargin = getDockMargin(layout, 'right')
	const marginsToSubtract = [leftMargin, rightMargin, px(siteMargin), px(siteMargin)].filter((v) => v && v !== '0')

	return marginsToSubtract.length > 0 ? `calc(100% - ${marginsToSubtract.join(' - ')})` : '100%'
}

const isHorizontallyDocked = (layout: Component['layout']): boolean => !!(layout?.docked?.left && layout?.docked?.right)
const isVerticallyDocked = (layout: Component['layout']): boolean => !!(layout?.docked?.top && layout?.docked?.bottom)

const StripColumnsContainer = createCompNode({
	getDependencies: (component: Component, refs: FeatureRefs<'compsMeasuresCss'>) => ({
		compProps: refs.propertyQuery<StripColumnsContainerDefinition['property']>(component.propertyQuery!),
		deviceType: envRefs.deviceType,
		absoluteComps: envRefs.meshRuntimeData_absoluteComps,
		siteWidth: envRefs.siteWidth,
		isMobileView: envRefs.isMobileView,
	}),
	toViewItem: (component, deps) => {
		const { id, layout } = component

		if (!deps.compProps?.fullWidth) {
			return { width: px(layout.width) }
		}
		if (deps.isMobileView) {
			return FULL_WIDTH_MOBILE_SITE_MEASURES
		}
		const {
			compProps: { siteMargin = 0 },
			siteWidth = DEFAULT_DESKTOP_SITE_WIDTH,
		} = deps

		const isAbsoluteStrip = !!deps.absoluteComps.includes(id)
		const isStripWiderThanSiteWidth = siteWidth < (layout.width || 0)
		const leftValueForAbsStrip = isStripWiderThanSiteWidth ? siteMargin : `calc((100% - ${px(siteWidth)}) * 0.5)`

		return _.pickBy(
			{
				left: isAbsoluteStrip ? leftValueForAbsStrip : siteMargin ? null : `0`,
				'margin-left': siteMargin ? `auto` : getDockMargin(layout, 'left') || '0',
				'margin-right': siteMargin ? `auto` : null,
				width: getFullWidthAfterMarginsSubtraction(layout, siteMargin),
				'min-width': isHorizontallyDocked(layout) ? 'initial' : px(siteWidth),
			},
			_.identity
		) as CompsLayout
	},
})

const fullWidth = createCompNode({
	getDependencies: (component: Component, refs: FeatureRefs<'compsMeasuresCss'>) => ({
		compProps: isComponentSlideShow(component)
			? refs.propertyQuery<SlideShowContainerDefinition['property']>(component.propertyQuery!)
			: undefined,
		siteWidth: envRefs.siteWidth,
		isMobileView: envRefs.isMobileView,
	}),
	toViewItem: (component, deps) => {
		const { isMobileView, siteWidth, compProps } = deps
		if (isComponentSlideShow(component) && compProps?.type !== 'StripContainerSlideShowProperties') {
			return defaultMeasures.toViewItem(component, deps)
		}
		const { layout, componentType } = component
		const compHeight = isContainer(componentType) ? undefined : layout.height

		if (isMobileView) {
			return { ...FULL_WIDTH_MOBILE_SITE_MEASURES, ...{ height: compHeight ? px(compHeight) : undefined } }
		}
		return _.pickBy(
			{
				left: '0',
				'margin-left': getDockMargin(layout, 'left') || '0',
				width: getFullWidthAfterMarginsSubtraction(layout),
				'min-width': isHorizontallyDocked(layout) ? 'initial' : px(siteWidth || DEFAULT_DESKTOP_SITE_WIDTH),
				height: compHeight ? px(compHeight) : undefined,
			},
			_.identity
		) as CompsLayout
	},
})

const getDockMargin = (layout: Component['layout'], dockKey: keyof Docking): string | null => {
	const dock = layout.docked?.[dockKey]
	const pxMargin = dock?.px
	if (pxMargin) {
		return px(pxMargin)
	}
	const vwMargin = dock?.vw
	if (vwMargin) {
		return `${vwMargin}vw`
	}
	return null
}

const defaultMeasures = createCompNode({
	getDependencies: () => ({
		isMobileView: envRefs.isMobileView,
	}),

	toViewItem: (component, deps) => getSimpleComponentMeasures(component, deps.isMobileView),
})

const WRichText = createCompNode({
	getDependencies: (component: Component, refs: FeatureRefs<'compsMeasuresCss'>) => ({
		isMobileView: envRefs.isMobileView,
		compProps: refs.propertyQuery<WRichTextProperties>(component.propertyQuery!),
	}),
	toViewItem: (component, { isMobileView, compProps }) => {
		const isVerticalText = compProps?.verticalText
		return {
			...getSimpleComponentMeasures(component, isMobileView),
			...(isVerticalText ? AUTO_WIDTH : AUTO_HEIGHT),
		}
	},
})

const getSimpleComponentMeasures = (component: Component, isMobileView: boolean): CompsLayout => {
	const { layout, componentType } = component
	const { width, height } = layout
	const widthObject = isHorizontallyDocked(layout)
		? isMobileView
			? FULL_WIDTH_MOBILE_SITE_MEASURES
			: {
					width: getFullWidthAfterMarginsSubtraction(layout),
					left: '0',
					'margin-left': getDockMargin(layout, 'left') || '0',
					'min-width': `initial`,
			  }
		: { width: px(width) }

	return isContainer(componentType) || isVerticallyDocked(layout)
		? widthObject
		: { ...widthObject, height: px(height) }
}

const compTypeToCompNodeMapper: Record<string, CompNode<any, CompsLayout, RenderableCompMeasures>> = {
	StripColumnsContainer,
	WRichText,

	SlideShowContainer: fullWidth,
	MasterPage: fullWidth,
	HeaderContainer: fullWidth,
	FooterContainer: fullWidth,
	PagesContainer: fullWidth,
	PageGroup: fullWidth,
	Page: fullWidth,
	ClassicSection: fullWidth,
	BgImageStrip: fullWidth,

	default: defaultMeasures,
}

const shouldNotCalculate = (component: Component) =>
	hasResponsiveLayout(component) || excludedComponents.has(component.componentType)

const compsMeasuresStyleCss = (editor: boolean) =>
	createCompNode({
		getDependencies: (component: Component, refs: FeatureRefs<'compsMeasuresCss'>) => {
			if (shouldNotCalculate(component)) {
				return null
			}
			const internalCompNode =
				compTypeToCompNodeMapper[component.componentType] || compTypeToCompNodeMapper.default
			return internalCompNode.getDependencies(component, refs)
		},
		toViewItem: (component, deps) => {
			if (shouldNotCalculate(component)) {
				return undefined
			}
			const internalCompNode =
				compTypeToCompNodeMapper[component.componentType] || compTypeToCompNodeMapper.default
			const { layout } = component
			const compMeasures = internalCompNode.toViewItem(component, deps)
			const compHeight = compMeasures.height

			return {
				...compMeasures,
				...(layout.rotationInDegrees && {
					transform: `rotate(${layout.rotationInDegrees}deg)`,
				}),
				...(component.id !== SOSP_CONTAINER_ID && getVisibility(component)),
				height:
					compHeight && editor && component.componentType === 'HoverBox'
						? `${compHeight} !important`
						: compHeight,
			}
		},
	})
export const compsMeasures = (editor: boolean) =>
	createCssCompNode(
		'compsMeasuresCss',
		'compsMeasures',
		withModes(compsMeasuresStyleCss(editor), OVERRIDEN_PROPS, mergeCSSModes)
	)

//* HOVER BOX LOGIC *
// size (height and width) should be translated to css only in the default selector.
// UNLESS- both hover and default are visible, in which case we need to write the size to both selectors.
// we need to determined what size should be written there.
// the determination is done by inspecting the visibility of each mode, when the visible mode/ override wins.
// if default selector !== default override and hover override doesn't exist, we need to create hover override and make it the same as default mode.
// default and hover overrides should get all their missing attributes from the default selector.
// we need to make default selector to be default override and delete default override.
// data-mode="default" should be displaying only the visibility attribute and the default selector should have no visibility attribute.

export const mergeCSSModes = (css: SelectorObj) => {
	const {
		[DEFAULT_DATA_MODE]: defaultOverride,
		[HOVER_DATA_MODE]: hoverOverride,
		[DEFAULT_SELECTOR]: initialDefaultSelector,
	} = css
	const hoverModeExistsInStructure = hoverOverride !== undefined
	if (defaultOverride && !areCssAttributesEqual(defaultOverride, initialDefaultSelector)) {
		css[HOVER_DATA_MODE] = mergeSelectors(initialDefaultSelector, hoverOverride)
	}
	const defaultVisibility = { visibility: defaultOverride?.visibility || initialDefaultSelector.visibility }
	const defaultOverrideWithoutVisibility = _.omit(defaultOverride, ['visibility'])

	css[DEFAULT_SELECTOR] = mergeSelectors(initialDefaultSelector, defaultOverrideWithoutVisibility, defaultVisibility)
	const defaultCss = css[DEFAULT_SELECTOR]
	const defaultCssWithoutSize = _.omit(defaultCss, SIZE)

	if (hoverOverride) {
		handleHoverMode(css, defaultCss, hoverOverride, hoverModeExistsInStructure, defaultCssWithoutSize)
	} else if (defaultOverride && !areCssAttributesEqual(defaultOverride, defaultCss)) {
		css[HOVER_DATA_MODE] = defaultCssWithoutSize
	}

	css[DEFAULT_DATA_MODE] = defaultVisibility

	removeVisibilityAttributes(css)

	return css
}

const handleHoverMode = (
	css: SelectorObj,
	defaultCss: CssObject,
	hoverOverride: CssObject,
	hoverModeExistsInStructure: boolean,
	defaultCssWithoutSize: CssObject
) => {
	if (defaultCss.visibility === 'hidden' && hoverModeExistsInStructure) {
		css[DEFAULT_SELECTOR] = mergeSelectors(defaultCss, _.omit(hoverOverride, ['visibility']))
	}

	css[HOVER_DATA_MODE] = determineHoverOverride(
		css,
		hoverOverride,
		defaultCssWithoutSize,
		_.omit(hoverOverride, SIZE)
	)

	const doesHoverModeAndDefaultSelectorContainOnlyVisibility = _.isEmpty({
		..._.omit(css[HOVER_DATA_MODE], ['visibility']),
		..._.omit(css[DEFAULT_SELECTOR], ['visibility']),
	})

	if (
		!doesHoverModeAndDefaultSelectorContainOnlyVisibility &&
		_.isEmpty(getDifferingAttributes(hoverOverride, defaultCss))
	) {
		delete css[HOVER_DATA_MODE]
	}
}

const determineHoverOverride = (
	css: SelectorObj,
	hoverOverride: CssObject,
	defaultCssWithoutSize: CssObject,
	hoverOverrideWithoutSize: CssObject
): CssObject => {
	if (hoverOverride.visibility === 'hidden') {
		return hoverOverrideWithoutSize
	}

	return areCssAttributesEqual(defaultCssWithoutSize, hoverOverrideWithoutSize)
		? _.pick(hoverOverride, SIZE)
		: getDifferingAttributes(hoverOverride, css[DEFAULT_SELECTOR])
}

const removeVisibilityAttributes = (css: SelectorObj) => {
	if (
		css[HOVER_DATA_MODE] &&
		css[DEFAULT_DATA_MODE] &&
		css[HOVER_DATA_MODE].visibility === css[DEFAULT_DATA_MODE].visibility
	) {
		css[DEFAULT_SELECTOR].visibility = css[HOVER_DATA_MODE].visibility
		delete css[HOVER_DATA_MODE].visibility
		delete css[DEFAULT_DATA_MODE].visibility
	} else {
		delete css[DEFAULT_SELECTOR].visibility
	}

	for (const selector in css) {
		if (css[selector].visibility === 'visible') {
			delete css[selector].visibility
		}
		if (selector !== DEFAULT_SELECTOR && _.isEmpty(css[selector])) {
			delete css[selector]
		}
	}
}
